/**
 * TV1-2071 — keep `useAuth().user.suspensionStatus` live, so the layout-level
 * suspension banner clears the moment a payment resumes the account.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * AC-2 is "on approval the account resumes without human action AND the UI
 * reflects it without a manual refresh". The `/subscription` screen's own
 * surfaces already satisfied it — `subscriptionDataPolicy` refetches on the
 * account signal, so the amount due settles to zero and the Pay button unmounts
 * on their own. The BANNER did not, and it is the most visible thing on the page:
 * the tenant paid, the screen said it worked, and a red bar kept telling them
 * they were suspended. A worrying success is as much a defect as a reassuring
 * failure.
 *
 * The banner (`contexts/useAuth/SuspensionBanner.tsx`) reads
 * `auth.user.suspensionStatus`, which arrives on `/users-profiles/me`. That field
 * is NOT projected onto the profile row — `usersProfiles.readmodel.ts` computes
 * it inside the `/me` query handler by cross-reading
 * `clients_management_accounts_readmodel` — and the `/me` realtime subscription
 * pushes only on `UserProfileRegistered` / `UserProfileUpdated` /
 * `UserDataErased`. `ClientAccountResumed` touches none of those streams, so
 * `useAuth` was never told and re-rendered nothing.
 *
 * ── The shape, and why it is this shape ──────────────────────────────────────
 *
 * Query-first is NOT this module's job (`useAuth` already hydrates `/me` on
 * mount). This is only the live-refresh half, and it mirrors the pattern
 * `subscribeToCapabilities` in `useAuth.tsx` already uses for exactly the same
 * problem — a field whose authoritative value must be re-READ rather than taken
 * from a pushed row:
 *
 *   signal → debounce → re-read the authoritative HTTP query → patch one field
 *
 * ⚠️ A pv3 signal-mode subscription does NOT auto-refetch. `{ type: 'signal' }`
 * carries no payload; the refetch is the subscriber's job. That is the whole
 * reason `fetchSuspensionStatus` is a dependency here and not an afterthought —
 * a version of this that merely listened would report success and change nothing.
 *
 * It patches ONLY `suspensionStatus`, via an updater that returns the previous
 * object unchanged when the status has not moved. Two reasons, both load-bearing:
 * replacing the whole user would clobber the capability map that
 * `useAuth` maintains as a deliberately separate concern, and any intermediate
 * render with a null/absent user would unmount and remount the banner — a flicker
 * on the one component whose job is to be calm.
 *
 * Dependencies are injected rather than reaching for `backend` directly so this
 * is unit-testable. That matters more than usual here: `src/contexts/**` is in
 * NO vitest `include` glob, so a test placed next to `useAuth.tsx` would never
 * run and would still report green. `src/infrastructure/**` IS collected, which
 * is why this file lives here and not there.
 */

/** The two states the account suspension gate recognises. */
export type SuspensionStatus = 'active' | 'suspended';

/**
 * ~200 ms, the web-app convention (`sizeGuidesDataPolicy`,
 * `subscriptionDataPolicy`, and `useAuth`'s own capability signal). Long enough
 * to coalesce the burst of account-row writes a resume produces into one re-read,
 * short enough that the banner disappears while the user is still looking at it.
 */
export const ACCOUNT_SUSPENSION_SIGNAL_DEBOUNCE_MS = 200;

export type AccountSuspensionSignalDeps = {
  /**
   * Open the account subscription in SIGNAL mode and invoke `onSignal` on each
   * ping. In `useAuth` this is
   * `backend.subscriptions.AccountsReadmodel({ _snapshot: false, _changes: 'signal' }, ...)`.
   */
  subscribe: ( onSignal: () => void ) => { close: () => void };
  /**
   * Re-read the authoritative status. In `useAuth` this is
   * `backend.queries.UsersProfilesMe({})` — the same endpoint the banner's value
   * came from, so there is exactly one source of truth for it.
   */
  fetchSuspensionStatus: () => Promise<SuspensionStatus | undefined>;
  /** Apply a status that actually changed. Never called with an unchanged value. */
  onStatus: ( status: SuspensionStatus ) => void;
  /** Overridable so tests need no timers. */
  debounceMs?: number;
};

/**
 * Wire the account signal to a debounced re-read of the suspension status.
 * Returns a closer that is safe to call more than once; after closing, an
 * in-flight re-read can no longer apply a status (a late response must not
 * resurrect a banner on a signed-out session).
 */
export const createAccountSuspensionSignal = ({
  subscribe,
  fetchSuspensionStatus,
  onStatus,
  debounceMs = ACCOUNT_SUSPENSION_SIGNAL_DEBOUNCE_MS
}: AccountSuspensionSignalDeps ): { close: () => void } => {
  let closed = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const reread = async (): Promise<void> => {
    try {
      const status = await fetchSuspensionStatus();
      // `closed` is re-checked AFTER the await: the subscription may have been
      // torn down (signout, unmount) while the request was in flight, and
      // applying a stale status then would contradict the current session.
      if ( closed || !status ) return;
      onStatus( status );
    } catch {
      // Transient gateway/network. Keep the current status — the next signal
      // re-reads. Failing loudly here would put an error in front of a user who
      // has just successfully paid.
    }
  };

  const subscription = subscribe(() => {
    if ( closed ) return;
    if ( timer ) clearTimeout( timer );
    timer = setTimeout(() => {
      timer = null;
      void reread();
    }, debounceMs );
  });

  return {
    close: () => {
      closed = true;
      if ( timer ) {
        clearTimeout( timer );
        timer = null;
      }
      subscription.close();
    }
  };
};
